/** @file Hand-built keyframe form: one key's frame and value, the curve and delete of all selected keys. */
import { useState, type ReactNode } from 'react';
import type { CommandCall, Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { choiceOptions } from '../../forms/choiceOptions';
import { FormStatus } from '../../forms/FormStatus';
import { submitCalls } from '../../forms/submitCalls';
import { selectionStore } from '../../stores/selectionStore';
import type { KeyRef } from '../../stores/types';
import { Button } from '../../ui-kit/Button';
import { Checkbox } from '../../ui-kit/Checkbox';
import { ColorField } from '../../ui-kit/ColorField';
import { NumberField } from '../../ui-kit/NumberField';
import { Select } from '../../ui-kit/Select';
import { CurveFields } from './CurveFields';
import { keyDetails, type KeyDetail, type KeyValueKind } from './keyDetails';
import { keyEditCalls } from './keyEditCalls';
import { keyStatus, type KeyStatus } from './keyStatus';

type SetValue = (value: unknown) => void;
/** Submits `calls`; after a success `next` (when given) becomes the key selection. */
type Run = (calls: CommandCall[], next?: readonly KeyRef[]) => void;
const NONE = '—';
const placeholders = (doc: Doc, slot: string): { value: string; label: string }[] =>
  [{ value: '', label: NONE }, ...[...new Set(Object.values(doc.skins).flatMap((s) => s.entries.filter((e) => e.slot === slot).map((e) => e.placeholder)))].sort().map((p) => ({ value: p, label: p }))];

const VALUE: Record<KeyValueKind, (d: KeyDetail, set: SetValue, doc: Doc, t: Translate) => ReactNode> = {
  number: (d, set, _doc, t) => <NumberField label={t('panels.inspector.key.value')} value={typeof d.value === 'number' ? d.value : 0} step={0.1} onCommit={set} />,
  color: (d, set, _doc, t) => <ColorField value={typeof d.value === 'string' ? d.value : '#000000'} alpha={false} labels={{ hex: t('panels.inspector.key.value'), pick: t('forms.color.pick'), alpha: t('forms.color.alpha') }} onCommit={set} />,
  flag: (d, set, _doc, t) => <Checkbox label={t('panels.inspector.key.value')} checked={d.value === true} onChange={set} />,
  inherit: (d, set, _doc, t) => <Select label={t('panels.inspector.key.value')} value={String(d.value)} options={choiceOptions('bone.setInherit', 'value').map((o) => ({ value: o, label: o }))} onChange={set} />,
  placeholder: (d, set, doc, t) => <Select label={t('panels.inspector.key.value')} value={typeof d.value === 'string' ? d.value : ''} options={placeholders(doc, d.ref.target)} onChange={(v) => { set(v === '' ? null : v); }} />,
  readonly: (_d, _set, _doc, t) => <p className="p2-inspector-note">{t('panels.inspector.key.inTimeline')}</p>,
};

/** Moving the key also moves it in the selection (so the form keeps showing it, and its status). */
function KeyValue({ doc, detail, run, t }: { doc: Doc; detail: KeyDetail; run: Run; t: Translate }) {
  const { ref } = detail;
  return (
    <>
      <NumberField label={t('panels.inspector.key.frame')} value={ref.frame} min={0} precision={0} onCommit={(f) => { run(keyEditCalls.move(ref, f), [{ ...ref, frame: f }]); }} />
      {VALUE[detail.kind](detail, (v) => { run(keyEditCalls.value(ref, v)); }, doc, t)}
    </>
  );
}

/** Deleting the keys clears the key selection; the curve shown is the one the selected keys share (Mixed otherwise).
 *  A status shows only while the keys it is about stay selected (keyStatus). */
export function KeyframeForm({ doc, keys, t }: { doc: Doc; keys: readonly KeyRef[]; t: Translate }) {
  const [status, setStatus] = useState<KeyStatus | null>(null);
  const run: Run = (calls, next) => {
    void submitCalls(calls).then((r) => {
      setStatus(keyStatus.after(r, keys, next));
      if (r.error === null && next !== undefined) selectionStore.setState({ keys: [...next] });
    });
  };
  const first = keys[0];
  const single = keys.length === 1 && first !== undefined ? keyDetails.detail(doc, first) : null;
  return (
    <div className="p2-inspector-entity">
      <h3 className="p2-inspector-kind">{t('panels.inspector.keysCount', { n: keys.length })}</h3>
      {single !== null && <KeyValue doc={doc} detail={single} run={run} t={t} />}
      {keys.some((k) => keyDetails.curved(k.channel)) && <CurveFields curve={keyDetails.sharedCurve(doc, keys)} t={t} onCommit={(c) => { run(keyEditCalls.curve(doc, keys, c)); }} />}
      <Button label={t('panels.inspector.key.remove')} danger onClick={() => { run(keyEditCalls.remove(keys), []); }} />
      <FormStatus status={keyStatus.shown(status, keys)} t={t} />
    </div>
  );
}
